import type { ButtonHTMLAttributes, ReactElement, ReactNode, Ref } from 'react'
import { cn } from '@/lib/cn'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
  fullWidth?: boolean
  ref?: Ref<HTMLButtonElement>
}

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-fg shadow-sheet hover:bg-accent-hover',
  secondary: 'border border-line-strong bg-surface text-fg shadow-sheet hover:bg-sunken',
  ghost: 'text-fg-muted hover:bg-sunken hover:text-fg',
  danger: 'bg-bad-700 text-on-solid shadow-sheet hover:bg-bad-800',
  success: 'bg-ok-700 text-on-solid shadow-sheet hover:bg-ok-800'
}

const shells: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-[0.9375rem]'
}

/** Icon sizing lives on the content span so it never fights the Spinner's own size. */
const contents: Record<ButtonSize, string> = {
  sm: 'gap-1.5 [&_svg]:size-3.5',
  md: 'gap-2 [&_svg]:size-4',
  lg: 'gap-2 [&_svg]:size-[1.125rem]'
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps): ReactElement {
  return (
    <button
      className={cn(
        'relative inline-flex select-none items-center justify-center whitespace-nowrap rounded-control font-semibold transition-colors duration-150 ease-out-quart',
        'disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none',
        variants[variant],
        shells[size],
        fullWidth && 'w-full',
        className
      )}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="absolute inset-0 grid place-items-center">
          <Spinner size={size === 'lg' ? 'md' : 'sm'} />
        </span>
      ) : null}
      {/* Kept in flow while loading so the button never changes width. */}
      <span className={cn('inline-flex items-center', contents[size], loading && 'invisible')}>
        {iconLeft ? (
          <span className="inline-flex shrink-0 items-center" aria-hidden="true">
            {iconLeft}
          </span>
        ) : null}
        {children}
        {iconRight ? (
          <span className="inline-flex shrink-0 items-center" aria-hidden="true">
            {iconRight}
          </span>
        ) : null}
      </span>
    </button>
  )
}
